"use client";

import { startTransition, useState } from "react";
import { useRouter } from "next/navigation";

type LearningPathFormProps = {
  initialValues: {
    currentLevel: string;
    weeklyHours: number;
    objective: string;
    deadlineWeeks: number | null;
    goalsText: string | null;
  };
};

type LearningPathResponse = {
  ok: boolean;
  learningPath?: {
    title: string;
  } | null;
  error?: string;
};

export function LearningPathForm({ initialValues }: LearningPathFormProps) {
  const router = useRouter();
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("loading");
    setMessage(null);

    const formData = new FormData(event.currentTarget);
    const payload = {
      currentLevel: String(formData.get("currentLevel") || "beginner"),
      weeklyHours: Number(formData.get("weeklyHours") || 6),
      objective: String(formData.get("objective") || "fullstack"),
      deadlineWeeks: Number(formData.get("deadlineWeeks") || 24),
      goalsText: String(formData.get("goalsText") || ""),
    };

    const response = await fetch("/api/learning-path", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = (await response.json()) as LearningPathResponse;

    if (!response.ok || !data.ok) {
      setStatus("error");
      setMessage(data.error || "No se pudo generar la ruta personalizada.");
      return;
    }

    setStatus("success");
    setMessage(
      data.learningPath?.title
        ? `Ruta actualizada: ${data.learningPath.title}`
        : "Ruta personalizada actualizada.",
    );
    startTransition(() => {
      router.refresh();
    });
  }

  return (
    <form onSubmit={handleSubmit} className="mt-4 space-y-4">
      <label className="block space-y-2">
        <span className="text-sm text-slate-300">Nivel actual</span>
        <select
          name="currentLevel"
          defaultValue={initialValues.currentLevel || "beginner"}
          className="w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none"
        >
          <option value="beginner">Principiante</option>
          <option value="intermediate">Intermedio</option>
          <option value="upskilling">Reciclaje / mejora</option>
        </select>
      </label>

      <label className="block space-y-2">
        <span className="text-sm text-slate-300">Horas por semana</span>
        <input
          name="weeklyHours"
          type="number"
          min="2"
          max="40"
          defaultValue={initialValues.weeklyHours || 6}
          className="w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none"
        />
      </label>

      <label className="block space-y-2">
        <span className="text-sm text-slate-300">Objetivo</span>
        <select
          name="objective"
          defaultValue={initialValues.objective || "fullstack"}
          className="w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none"
        >
          <option value="fullstack">Full stack DAW</option>
          <option value="backend">Backend DAW</option>
          <option value="frontend">Frontend DAW</option>
        </select>
      </label>

      <label className="block space-y-2">
        <span className="text-sm text-slate-300">Plazo estimado en semanas</span>
        <input
          name="deadlineWeeks"
          type="number"
          min="4"
          max="52"
          defaultValue={initialValues.deadlineWeeks || 24}
          className="w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none"
        />
      </label>

      <label className="block space-y-2">
        <span className="text-sm text-slate-300">Objetivo concreto</span>
        <textarea
          name="goalsText"
          rows={4}
          defaultValue={
            initialValues.goalsText || "Quiero completar una ruta DAW con proyectos reales y portfolio."
          }
          className="w-full rounded-2xl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none"
        />
      </label>

      <button
        type="submit"
        disabled={status === "loading"}
        className="inline-flex rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === "loading" ? "Generando..." : "Generar ruta personalizada"}
      </button>

      {message ? (
        <div
          className={`rounded-3xl p-4 text-sm leading-6 ${
            status === "error"
              ? "border border-rose-300/16 bg-rose-300/10 text-rose-100"
              : "border border-teal-300/16 bg-teal-300/10 text-teal-100"
          }`}
        >
          {message}
        </div>
      ) : null}
    </form>
  );
}
